import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import type { JSX, KeyboardEvent } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation } from '@/api/query'
import { Button } from '@/ui/Button'
import { TextField } from '@/ui/Field'
import { Empty, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'
import { useOnboarding, useWizard } from './context'
import { WizardChrome, WizardFooter } from './WizardLayout'
import { pathFor } from './steps'
import type { SuggestionOut } from './steps'

type OnboardingPack = Schema<'OnboardingPackOut'>

/** The bands S-D2 draws, with the heading and the aside for each. */
/** Where a keyword came from — the API's own two values, not a free string. */
type Source = 'suggested' | 'custom'

/** The screen 5∅ answers, sent only when they were given. */
interface Answers {
  sells?: string
  serves?: string
  competitor_domain?: string
}

const GROUPS: { key: string; title: string; aside: string }[] = [
  { key: 'quick_wins', title: 'Already ranking — quick wins', aside: 'positions 11–30' },
  { key: 'buyer_intent', title: 'Buyer intent', aside: 'people ready to spend' },
  { key: 'competitor_gap', title: "Competitors rank, you don't", aside: 'the gap' },
  { key: 'seed', title: 'What you do', aside: 'from your own description' },
]

/**
 * Step 5 — S-D2's `s5` and `s5-empty`.
 *
 * **Pre-selected, not blank.** Most tenants do not know which keywords to choose
 * — that is why they bought this — so what the suggester returns arrives ticked,
 * and the count against the plan limit is visible while choosing rather than
 * enforced at submit.
 *
 * **An empty suggestion list is the `5∅` screen, not an error.** A site under a
 * few months old has no Search Console history to mine, which is legitimate and
 * common. The two questions it asks are what makes a second call productive, and
 * the answers are kept on the step so S-B24 can run the competitor gap without
 * asking again.
 */
export function KeywordsStep(): JSX.Element {
  const state = useWizard()
  const { adopt } = useOnboarding()
  const navigate = useNavigate()
  const toast = useToast()

  const [suggestions, setSuggestions] = useState<SuggestionOut[] | null>(null)
  const [chosen, setChosen] = useState<Map<string, Source>>(
    () =>
      new Map(
        state.keywords
          .filter((row) => row.enabled)
          .map((row) => [row.keyword, row.source === 'suggested' ? 'suggested' : 'custom']),
      ),
  )
  const [typed, setTyped] = useState('')
  const [sells, setSells] = useState('')
  const [serves, setServes] = useState('')
  const [competitor, setCompetitor] = useState('')

  const suggest = useMutation((body: Answers) =>
    call(() => api.POST('/api/v1/onboarding/keywords/suggestions', { body })),
  )
  const save = useMutation((body: { keywords: { keyword: string; source: Source }[] }) =>
    call(() => api.PUT('/api/v1/onboarding/keywords', { body })),
  )

  // Asked once on arrival, from what step 1 already knows. The answer decides
  // which of the two screens this is.
  const asked = useRef(false)
  useEffect(() => {
    if (asked.current) return
    asked.current = true
    void (async () => {
      const result = await suggest.run({})
      if (result.ok) accept(result.data.suggestions)
    })()
    // `accept` closes over `suggest`, stable for the component's life; the guard
    // above is what makes this run once regardless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Take a suggestion list and tick what the server ticked.
   *
   * The ticking is not cosmetic: S-D2 arrives pre-selected because most tenants
   * do not know which keywords to choose, and a list that rendered ticked chips
   * while `chosen` was empty would save nothing when they pressed Continue.
   */
  function accept(items: SuggestionOut[]): void {
    setSuggestions(items)
    setChosen((current) => {
      const next = new Map(current)
      for (const item of items) {
        if (item.selected) next.set(item.keyword, 'suggested')
      }
      return next
    })
  }

  function toggle(keyword: string, source: Source): void {
    setChosen((current) => {
      const next = new Map(current)
      if (next.has(keyword)) next.delete(keyword)
      else next.set(keyword, source)
      return next
    })
  }

  /** The keyword sitting in *Add your own*, if it is long enough to be one. */
  function pending(): string | null {
    const keyword = typed.trim().toLowerCase()
    return keyword.length < 2 ? null : keyword
  }

  function addTyped(): void {
    const keyword = pending()
    if (keyword === null) return
    setChosen((current) => new Map(current).set(keyword, 'custom'))
    setTyped('')
  }

  async function onBuildList(): Promise<void> {
    // Built by omission rather than by assigning `undefined`: under
    // `exactOptionalPropertyTypes` an explicit `undefined` is not an absent key,
    // and the route treats an absent answer as "not given" while a present one
    // is merged onto what the step already holds.
    const answers: Answers = {}
    if (sells.trim()) answers.sells = sells.trim()
    if (serves.trim()) answers.serves = serves.trim()
    if (competitor.trim()) answers.competitor_domain = competitor.trim()
    const result = await suggest.run(answers)
    if (result.ok) accept(result.data.suggestions)
  }

  /**
   * Save, then go wherever the server's cursor now points.
   *
   * **Unless it points here, which is the ordinary case for a tenant who has
   * already finished setup.** `_settle` advances the cursor only while something
   * is outstanding — *"nothing outstanding: stay on the last step rather than
   * inventing a seventh"* — so a returning tenant adding a keyword gets a
   * `current_step` of `keywords` back, and `navigate` to the page you are
   * already on does nothing at all. The save had succeeded; Continue read as a
   * dead button, which is precisely the failure `useToast` names in its own
   * refusal to be a no-op. Confirm it instead.
   *
   * `may_complete` is deliberately not consulted here the way `BrandVoiceStep`
   * consults it: it means *every required step is done*, which is true from step
   * 1 onward, so branching on it would jump a mid-wizard tenant straight past
   * brand voice. Whether the cursor **moved** is the question, and it is the one
   * asked.
   *
   * **Whatever is still in the *Add your own* box is committed first.** The field
   * adds on Enter and said so, and Continue still threw the text away — a tenant
   * who typed their keyword and pressed the primary button watched it vanish and
   * the step settle with nothing in it. Losing typed input at submit is not a
   * hint the label can fix.
   */
  async function onContinue(): Promise<void> {
    const keywords = new Map(chosen)
    const uncommitted = pending()
    if (uncommitted !== null) keywords.set(uncommitted, 'custom')
    const result = await save.run({
      keywords: [...keywords].map(([keyword, source]) => ({ keyword, source })),
    })
    if (!result.ok) return
    setChosen(keywords)
    setTyped('')
    adopt(result.data)
    const next = pathFor(result.data.current_step)
    if (next !== pathFor('keywords')) {
      void navigate(next)
      return
    }
    toast.show({
      kind: 'success',
      title: `${String(keywords.size)} keyword${keywords.size === 1 ? '' : 's'} saved`,
      body: 'The next rank run will measure where you sit for them.',
    })
  }

  // The first call has not answered yet: a skeleton the shape of the chips,
  // rather than an empty screen that then fills.
  if (suggestions === null && suggest.pending) {
    return (
      <div className="app-wiz">
        <WizardChrome current="keywords" />
        <Loading label="Looking for keywords worth tracking" lines={4} />
      </div>
    )
  }

  const empty = suggestions !== null && suggestions.length === 0

  return (
    <div className="app-wiz">
      <WizardChrome current="keywords" />

      <section className="ar-card">
        <div className="ar-card__header">
          <h3>
            {empty
              ? 'Your site is too new for us to guess yet'
              : `We found ${String(suggestions?.length ?? 0)} keywords worth tracking`}
          </h3>
          <span className="ar-card__spacer" />
          <span className="ar-badge ar-badge--neutral">
            <span className="ar-num">{chosen.size}</span>&nbsp;selected
          </span>
        </div>

        <div className="ar-card__body ar-stack">
          <InlineError failure={suggest.state.failure ?? save.state.failure} />

          {empty ? (
            <>
              <Empty
                icon="⌕"
                title="Nothing to learn from yet"
                body="There's no Search Console history to mine, which is normal for a site under a few months old. Tell us two things and we'll build the list from scratch."
              />
              <div className="ar-stack" style={{ maxWidth: '34rem', margin: '0 auto' }}>
                <TextField
                  label="What do you sell?"
                  autoFocus
                  placeholder="e.g. modular kitchens, wardrobes, full-home interiors"
                  value={sells}
                  onChange={(event) => {
                    setSells(event.target.value)
                  }}
                />
                <TextField
                  label="Where are your customers?"
                  placeholder="e.g. Hyderabad, Secunderabad"
                  value={serves}
                  onChange={(event) => {
                    setServes(event.target.value)
                  }}
                />
                <TextField
                  label="Any competitor you'd like to beat? (optional)"
                  placeholder="competitor.in"
                  help="We'll pull the keywords they rank for and find your gaps."
                  value={competitor}
                  onChange={(event) => {
                    setCompetitor(event.target.value)
                  }}
                />
                <Button
                  tone="primary"
                  block
                  busy={suggest.pending}
                  busyLabel="Building your list"
                  disabled={!sells.trim()}
                  onClick={() => {
                    void onBuildList()
                  }}
                >
                  Build my keyword list
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="ar-muted" style={{ margin: 0 }}>
                Based on your site&rsquo;s content and what you already rank for. Tap to add or
                remove — you can change these any time.
              </p>

              {GROUPS.map((group) => {
                const inGroup = (suggestions ?? []).filter((item) => item.group === group.key)
                if (inGroup.length === 0) return null
                return (
                  <div key={group.key}>
                    <div className="ar-row" style={{ marginBottom: 'var(--ar-space-3)' }}>
                      <strong style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
                        {group.title}
                      </strong>
                      <span
                        className="ar-subtle"
                        style={{ fontSize: 'var(--ar-text-caption-size)' }}
                      >
                        {group.aside}
                      </span>
                    </div>
                    <div className="app-chips">
                      {inGroup.map((item) => (
                        <button
                          key={item.keyword}
                          type="button"
                          className="app-chip"
                          aria-pressed={chosen.has(item.keyword)}
                          onClick={() => {
                            toggle(item.keyword, 'suggested')
                          }}
                        >
                          {item.keyword}
                          {item.volume === null ? null : (
                            <span className="app-chip__vol">{item.volume}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )
              })}

              <CustomKeywords chosen={chosen} onRemove={toggle} />

              <OutreachPacks />

              <TextField
                label="Add your own"
                placeholder="Type a keyword and press Enter"
                value={typed}
                onChange={(event) => {
                  setTyped(event.target.value)
                }}
                onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                  if (event.key !== 'Enter') return
                  // The step is not a form, but a wizard *is* a sequence of
                  // Enter presses; without this, Enter would submit nothing and
                  // read as the field being broken.
                  event.preventDefault()
                  addTyped()
                }}
              />
            </>
          )}
        </div>

        {/* **No Continue on the 5∅ screen with nothing chosen**, which is what
            S-D2 draws: `#s5-empty` has no wizard footer control at all, only
            *Build my keyword list*. The implementation rendered one anyway, and
            it settled the step `done` having saved an empty set — so the rail
            showed Keywords ticked while the site tracked nothing, and every rank
            run afterwards reported `tracked_keywords: 0` with no visible reason.
            That state was reached in production on 2026-08-10.

            `chosen.size` rather than `empty` alone: a tenant who already has
            keywords and whose suggester happens to return nothing this time must
            still be able to re-save them. `WizardFooter` keeps rendering Back and
            *Skip for now* either way — the step is optional, and skipping it is
            the honest way past a list that could not be built. */}
        <WizardFooter current="keywords">
          {empty && chosen.size === 0 ? null : (
            <Button
              tone="primary"
              busy={save.pending}
              busyLabel="Saving your keywords"
              onClick={() => {
                void onContinue()
              }}
            >
              Continue
            </Button>
          )}
        </WizardFooter>
      </section>
    </div>
  )
}

/** Keywords the tenant typed, which appear in no suggestion band. */
function CustomKeywords({
  chosen,
  onRemove,
}: {
  chosen: Map<string, Source>
  onRemove: (keyword: string, source: Source) => void
}): JSX.Element | null {
  const custom = [...chosen].filter(([, source]) => source === 'custom').map(([keyword]) => keyword)
  if (custom.length === 0) return null

  return (
    <div>
      <div className="ar-row" style={{ marginBottom: 'var(--ar-space-3)' }}>
        <strong style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>Yours</strong>
        <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
          added by you
        </span>
      </div>
      <div className="app-chips">
        {custom.map((keyword) => (
          <button
            key={keyword}
            type="button"
            className="app-chip"
            aria-pressed="true"
            onClick={() => {
              onRemove(keyword, 'custom')
            }}
          >
            {keyword}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * The outreach half of step 5 — niche keyword packs (S-B21).
 *
 * **The SEO half of this step has existed since S-B9 and the outreach half never
 * did.** `app/db/onboarding.py` pins `SEO_MODULE` on every keyword it writes, so
 * a tenant finished the wizard with tracked SEO terms and nothing at all for post
 * search — and then met a discovery run with no keywords to queue. This is the
 * deliverable's *"suggested-keyword surface in the wizard's keyword step"*.
 *
 * **Packs rather than individual terms, because a brand-new tenant has no
 * history.** `#packs`'s *Suggested for you* is drawn from the posts that produced
 * a tenant's last 20 replies, and a tenant standing in the wizard has none. A
 * curated pack is the answer to *what do I start from on day one*, which is the
 * whole of S-B21's exit criterion.
 *
 * **It does not gate Continue.** Step 5's *done* is the SEO keyword selection;
 * taking a pack is an extra on the same screen. A tenant who takes no pack has
 * skipped nothing — they can add one from Outreach → Niche packs whenever they
 * like, and the empty state there says so.
 */
function OutreachPacks(): JSX.Element | null {
  const toast = useToast()
  const [packs, setPacks] = useState<OnboardingPack[] | null>(null)
  const [matched, setMatched] = useState(false)

  const load = useMutation(() => call(() => api.GET('/api/v1/onboarding/packs')))
  const take = useMutation((slug: string) =>
    call(() => api.POST('/api/v1/onboarding/packs/{slug}', { params: { path: { slug } } })),
  )

  const asked = useRef(false)
  useEffect(() => {
    if (asked.current) return
    asked.current = true
    void (async () => {
      const result = await load.run(undefined)
      if (result.ok) {
        setPacks(result.data.packs)
        setMatched(result.data.matched)
      }
    })()
    // Once, on arrival — the same guard the suggestion call above uses.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function onTake(pack: OnboardingPack): Promise<void> {
    const result = await take.run(pack.slug)
    if (result.ok) {
      toast.show({
        kind: 'success',
        title: `Added ${String(result.data.keywords_added)} outreach keywords.`,
        body: `From the ${pack.name} pack. You can change them in Outreach → Niche packs.`,
      })
      const refreshed = await load.run(undefined)
      if (refreshed.ok) setPacks(refreshed.data.packs)
    }
  }

  // Null while the first call is in flight, so the step does not flash an empty
  // section above the footer. A failure renders nothing at all rather than an
  // error: this is an optional extra on an optional step, and a red box here
  // would read as the wizard being broken.
  if (packs === null || packs.length === 0) return null

  return (
    <div>
      <div className="ar-row" style={{ marginBottom: 'var(--ar-space-3)' }}>
        <strong style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
          Who to look for on LinkedIn
        </strong>
        <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
          {matched ? 'closest to what you do, first' : 'pick the closest'}
        </span>
      </div>
      <p className="ar-muted" style={{ margin: '0 0 var(--ar-space-3)' }}>
        A curated set of phrases buyers post when they are shortlisting. Optional, and you can
        change every one of them later.
      </p>
      <div className="ar-stack ar-stack--tight">
        {packs.map((pack) => (
          <div className="app-conn" key={pack.slug}>
            <span className="app-conn__logo" aria-hidden="true">
              {pack.icon}
            </span>
            <span className="app-conn__body">
              <strong>{pack.name}</strong>
              <span>
                {pack.vertical} · {pack.keyword_count} keywords
              </span>
            </span>
            {pack.active ? (
              <span className="ar-badge ar-badge--success">✓ Added</span>
            ) : (
              <Button
                size="sm"
                busy={take.pending}
                onClick={() => {
                  void onTake(pack)
                }}
              >
                Add
              </Button>
            )}
          </div>
        ))}
      </div>
      <InlineError failure={take.state.failure} />
    </div>
  )
}
